import { Link } from 'react-router-dom';
import FadeUp from '../components/FadeUp';
import ImagePlaceholder from '../components/ImagePlaceholder';

const values = [
  {
    icon: '🌿',
    title: 'Fresh Daily',
    desc: 'We bring in fresh vegetables and produce regularly so you always find quality ingredients that are at their best.',
  },
  {
    icon: '🌎',
    title: 'Wide Range',
    desc: 'From everyday staples to specialty international ingredients, we stock a wide and ever-growing range to suit every kitchen.',
  },
  {
    icon: '🏡',
    title: 'Local Pride',
    desc: 'We are a proudly local Whanganui business. Our community matters to us and we love being part of it every day.',
  },
];

export default function About() {
  const styles = {
    page: { paddingTop: '68px' },
    hero: {
      background: '#C0392B',
      padding: '100px 24px 64px',
      textAlign: 'center',
    },
    heroHeading: {
      fontFamily: 'Poppins, sans-serif',
      fontWeight: 800,
      fontSize: 'clamp(36px, 5vw, 54px)',
      color: '#FFFFFF',
      marginBottom: '12px',
    },
    heroAccent: {
      fontFamily: 'Inter, sans-serif',
      fontSize: '18px',
      color: '#E67E22',
      fontWeight: 600,
    },
    storySection: {
      background: '#FFFFFF',
      padding: '88px 24px',
    },
    storyInner: {
      maxWidth: '1200px',
      margin: '0 auto',
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '64px',
      alignItems: 'center',
    },
    sectionLabel: {
      fontFamily: 'Inter, sans-serif',
      fontSize: '13px',
      fontWeight: 600,
      color: '#E67E22',
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
      marginBottom: '10px',
    },
    storyHeading: {
      fontFamily: 'Poppins, sans-serif',
      fontWeight: 800,
      fontSize: 'clamp(26px, 3.5vw, 38px)',
      color: '#1A1A1A',
      marginBottom: '20px',
    },
    storyParagraph: {
      fontFamily: 'Inter, sans-serif',
      fontSize: '16px',
      color: '#444',
      lineHeight: 1.8,
      marginBottom: '16px',
    },
    valuesSection: {
      background: '#FDF6EC',
      padding: '88px 24px',
    },
    valuesInner: {
      maxWidth: '1200px',
      margin: '0 auto',
    },
    valuesHeading: {
      fontFamily: 'Poppins, sans-serif',
      fontWeight: 800,
      fontSize: 'clamp(26px, 3.5vw, 38px)',
      color: '#1A1A1A',
      marginBottom: '48px',
      textAlign: 'center',
    },
    valuesGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
      gap: '28px',
    },
    valueCard: {
      background: '#FFFFFF',
      borderRadius: '16px',
      padding: '36px 28px',
      border: '1px solid rgba(0,0,0,0.06)',
      transition: 'transform 0.3s, box-shadow 0.3s',
      textAlign: 'center',
    },
    valueIcon: {
      fontSize: '40px',
      marginBottom: '16px',
    },
    valueTitle: {
      fontFamily: 'Poppins, sans-serif',
      fontWeight: 700,
      fontSize: '20px',
      color: '#1A1A1A',
      marginBottom: '10px',
    },
    valueDesc: {
      fontFamily: 'Inter, sans-serif',
      fontSize: '15px',
      color: '#555',
      lineHeight: 1.7,
    },
    ctaSection: {
      background: '#C0392B',
      padding: '80px 24px',
      textAlign: 'center',
    },
    ctaHeading: {
      fontFamily: 'Poppins, sans-serif',
      fontWeight: 800,
      fontSize: 'clamp(26px, 4vw, 38px)',
      color: '#FFFFFF',
      marginBottom: '12px',
    },
    ctaSub: {
      fontFamily: 'Inter, sans-serif',
      fontSize: '17px',
      color: 'rgba(255,255,255,0.8)',
      marginBottom: '32px',
    },
    ctaBtn: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      background: '#FFFFFF',
      color: '#C0392B',
      fontFamily: 'Inter, sans-serif',
      fontWeight: 700,
      fontSize: '15px',
      padding: '14px 32px',
      borderRadius: '100px',
      textDecoration: 'none',
      transition: 'background 0.2s, transform 0.2s',
    },
  };

  return (
    <div style={styles.page}>
      {/* Hero */}
      <section style={styles.hero} aria-label="About us hero">
        <FadeUp>
          <h1 style={styles.heroHeading}>About Us</h1>
          <p style={styles.heroAccent}>Proudly Serving Whanganui</p>
        </FadeUp>
      </section>

      {/* Story section */}
      <section style={styles.storySection} aria-label="Our story">
        <div style={styles.storyInner} className="about-story-grid">
          <FadeUp>
            <p style={styles.sectionLabel}>Our Story</p>
            <h2 style={styles.storyHeading}>A Whanganui Store with Heart</h2>
            <p style={styles.storyParagraph}>
              Whanganui Spices and Veges is a local supermarket and convenience store right in the heart of Whanganui, New Zealand. We specialise in fresh vegetables, premium spices and a wide range of international grocery products that reflect the diverse and vibrant community we serve.
            </p>
            <p style={styles.storyParagraph}>
              Trading as Shiriwastow Enterprises Ltd, we started with a simple idea: to give Whanganui families access to quality produce and authentic international ingredients without having to travel far or pay too much. Whether you are cooking a family favourite or exploring a new recipe, we have what you need.
            </p>
            <p style={styles.storyParagraph}>
              Our shelves are stocked with care, from fragrant whole spices and freshly arrived vegetables to the canned goods, lentils and sauces that form the backbone of so many beloved dishes. We are a multicultural store serving a multicultural community, and we take great pride in that.
            </p>
            <p style={styles.storyParagraph}>
              Come in and say hello. We look forward to seeing you in store.
            </p>
          </FadeUp>
          <FadeUp delay={0.15}>
            <ImagePlaceholder
              label="PHOTO: Owner or team photo in store, surrounded by products"
              height={420}
              borderRadius="20px"
            />
          </FadeUp>
        </div>
      </section>

      {/* Values */}
      <section style={styles.valuesSection} aria-label="Our values">
        <div style={styles.valuesInner}>
          <FadeUp>
            <p style={{ ...styles.sectionLabel, textAlign: 'center' }}>What We Stand For</p>
            <h2 style={styles.valuesHeading}>Our Values</h2>
          </FadeUp>
          <div style={styles.valuesGrid}>
            {values.map((v, i) => (
              <FadeUp key={v.title} delay={i * 0.1}>
                <div
                  style={styles.valueCard}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-6px)';
                    e.currentTarget.style.boxShadow = '0 16px 48px rgba(0,0,0,0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div style={styles.valueIcon} role="img" aria-label={v.title}>{v.icon}</div>
                  <h3 style={styles.valueTitle}>{v.title}</h3>
                  <p style={styles.valueDesc}>{v.desc}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={styles.ctaSection} aria-label="Contact call to action">
        <FadeUp>
          <h2 style={styles.ctaHeading}>We Would Love to Hear From You</h2>
          <p style={styles.ctaSub}>Get in touch with our friendly team in Whanganui.</p>
          <Link
            to="/contact"
            style={styles.ctaBtn}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#FDF6EC'; e.currentTarget.style.transform = 'scale(1.03)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#FFFFFF'; e.currentTarget.style.transform = 'scale(1)'; }}
          >
            Contact Us &#8594;
          </Link>
        </FadeUp>
      </section>

      <style>{`
        @media (max-width: 768px) {
          .about-story-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}
